// A copy of the backend's src/modules/access/types.ts (onengine-backend). Change both together.

export const PERMISSION_ACTIONS = [
  "view",
  "create",
  "edit",
  "reveal",
  "delete",
  "approve",
  "reject",
  "request",
  "export",
  "assign",
] as const;
export type PermissionAction = (typeof PERMISSION_ACTIONS)[number];

export type RoleLevel = "entity" | "servengine" | "both" | "servengine_all";

export const SCOPE_BOXES = [
  "entities",
  "clients",
  "brand",
  "priority",
  "site",
  "work_order_type",
  "trade",
  "subtrade",
  "state",
] as const;
export type ScopeBox = (typeof SCOPE_BOXES)[number];
export type ScopeKind = "required" | "optional" | "not_used";
export type RoleScopeKinds = Partial<Record<ScopeBox, ScopeKind>>;

export type Login = { kind: "company"; tenantId: string } | { kind: "servengine" };

export interface AccessQuery {
  userId: string;
  login: Login;
  now?: Date;
}

export interface SwitchRef {
  key: string;
  action: PermissionAction;
}
export const switchId = (s: SwitchRef): string => `${s.key}:${s.action}`;

// A role created on a path that skips the code rule has no code until the backend's post-launch cleanup.
export type SwitchSource =
  | { kind: "role"; roleId: string; roleCode: string | null; roleTenantId: string }
  | {
      kind: "temporary_role";
      roleId: string;
      roleCode: string | null;
      roleTenantId: string;
      userRoleId: string;
      endsOn: string;
    }
  | { kind: "adjustment"; adjustmentId: string; reviewAt: string; endsOn: string | null };

export interface SwitchGrant extends SwitchRef {
  sources: SwitchSource[];
}

export interface SwitchRemoval extends SwitchRef {
  by: Extract<SwitchSource, { kind: "adjustment" }>;
  overriddenBy: Extract<SwitchSource, { kind: "temporary_role" }> | null;
}

export interface HeldRole {
  userRoleId: string;
  roleId: string;
  roleCode: string | null;
  roleName: string;
  roleTenantId: string;
  level: RoleLevel;
  temporary: boolean;
  endsOn: string | null;
  // An ended role stays listed for display and grants nothing.
  ended: boolean;
  appliesAtThisLogin: boolean;
}

export type Bypass = "super_user" | "entity_admin" | null;

export interface CompanyAccess {
  tenantId: string;
  bypass: Bypass;
  roles: HeldRole[];
  switches: Record<string, SwitchGrant>;
  removed: SwitchRemoval[];
  boxesInPlay: Partial<Record<ScopeBox, ScopeKind>>;
  oneEntityOnly: boolean;
  earliestEndAt: string | null;
}

export interface EffectiveAccess {
  userId: string;
  login: Login;
  computedAt: string;
  superUser: boolean;
  serviceProvider: boolean;
  everyCompany: boolean;
  companies: Record<string, CompanyAccess>;
}

export type EffectiveAccessResponse = { success: true; data: EffectiveAccess };
